import { useMemo } from 'react';
import { css } from '@emotion/react';
import { ScheduleDefinition } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_definitions.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, DropdownField, Heading, ModalControlModalProps, NakedButton, Tray } from '@frontend/design-system';
import { useAvailabilityRuleForm } from '../../../hooks/settings/use-availability-rule-form';
import { useAvailabilityRuleFormData } from '../../../hooks/settings/use-availability-rule-form-data';
import { useRuleRecurrenceForm } from '../../../hooks/settings/use-rule-recurrence-form';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { AvailabilitySettingsTrackingIds } from '../../../trackingIds/availability-settings-tracking';
import type {
  AvailabilityMode,
  BookingChannelEnablement,
  GetAvailabilityRuleFormFieldProps,
} from '../availability-form/types';
import { AvailabilityRuleForm } from './availability-rule-form';

type Props = {
  locationId?: string;
  selectedScheduleDefinition: ScheduleDefinition | null;
  modalProps: ModalControlModalProps;
  availabilityMode: AvailabilityMode;
  onClose: () => void;
  isEditMode: boolean | undefined;
  selectedResourceId: string | undefined;
  bookingChannelEnablement: BookingChannelEnablement;
};

export const AddEditAvailabilityRuleTray = ({
  locationId,
  selectedScheduleDefinition,
  modalProps,
  availabilityMode,
  selectedResourceId,
  onClose,
  isEditMode = false,
  bookingChannelEnablement,
}: Props) => {
  const { t } = useTranslation('schedule-settings');
  const { isMultiLocation, getScopeName } = useSchedulingLocationInfo();
  const showLocationChip = isMultiLocation && locationId;

  const { providerList, operatoryList } = useAvailabilityRuleFormData({
    locationId,
  });

  const { values, reset, getFieldProps, isComplete, isSubmitting, handleSubmit, handleRRuleChange, rRule } =
    useAvailabilityRuleForm({
      locationId,
      selectedScheduleDefinition,
      availabilityMode,
      selectedResourceId,
      modalProps,
      isEditMode,
      providerList,
      operatoryList,
    });

  const ruleRecurrenceFormProps = useRuleRecurrenceForm({
    isAvailabilityRule: true,
    locationId,
    getAvailabilityRuleFieldProps: getFieldProps,
    values,
    rRule,
    handleRRuleChange,
  });

  const isVisibleBookingChannelWithoutServices = useMemo(() => {
    const hasBookingChannels = (values.bookingChannels?.length ?? 0) > 0;
    const hasSelectedServices = (values.services?.length || 0) > 0;
    return hasBookingChannels && !hasSelectedServices;
  }, [values.bookingChannels, values.services]);

  const closeModal = () => {
    reset();
    onClose();
  };

  const onCancel = () => {
    if (locationId) {
      SchedulerV3.trackPendoEvent(AvailabilitySettingsTrackingIds.events.ruleConfigCancelClicked, {
        ruleType: 'availability',
        locationId,
      });
    }
    closeModal();
  };

  return (
    <Tray {...modalProps} width='large' onClose={closeModal}>
      <Tray.Header
        Buttons={
          <NakedButton onClick={onCancel}>
            <Icon name='x' size={24} />
          </NakedButton>
        }
      >
        <div css={{ display: 'flex', flexDirection: 'column' }}>
          <TrayHeading
            getFieldProps={getFieldProps}
            isEditMode={isEditMode}
            providerList={providerList}
            operatoryList={operatoryList}
            availabilityMode={availabilityMode}
          />
          {showLocationChip && <Chip.Location maxWidth={'fit-content'}>{getScopeName(locationId)}</Chip.Location>}
        </div>
      </Tray.Header>
      <Tray.Body css={styles.trayBody}>
        <AvailabilityRuleForm
          locationId={locationId}
          providerList={providerList}
          operatoryList={operatoryList}
          getFieldProps={getFieldProps}
          ruleRecurrenceFormProps={ruleRecurrenceFormProps}
          availabilityMode={availabilityMode}
          isBookingRuleWithoutServices={isVisibleBookingChannelWithoutServices}
          bookingChannelEnablement={bookingChannelEnablement}
        />
      </Tray.Body>
      <Tray.Actions
        disabledPrimary={!isComplete || isSubmitting || isVisibleBookingChannelWithoutServices}
        primaryLabel={t('Save')}
        onPrimaryClick={handleSubmit}
        secondaryLabel={t('Cancel')}
        onSecondaryClick={onCancel}
      />
    </Tray>
  );
};

const TrayHeading = ({
  getFieldProps,
  isEditMode,
  providerList,
  operatoryList,
  availabilityMode,
}: {
  getFieldProps: GetAvailabilityRuleFormFieldProps;
  isEditMode: boolean;
  providerList: { value: string; label: string }[];
  operatoryList: { value: string; label: string }[];
  availabilityMode: AvailabilityMode;
}) => {
  const { t } = useTranslation('schedule-settings');
  const fieldProps =
    availabilityMode === 'PROVIDER' ? getFieldProps('serviceableProvider') : getFieldProps('operatory');
  const dropdownList = availabilityMode === 'PROVIDER' ? providerList : operatoryList;

  const title = useMemo(() => {
    const actionText = isEditMode ? t('Edit') : t('Add');
    const availabilityText = t('Availability for');

    return `${actionText} ${availabilityText}`;
  }, [isEditMode, t]);

  return (
    <div css={styles.trayHeading}>
      <Heading level={2}>{title}</Heading>
      <DropdownField label={''} {...fieldProps} css={styles.trayHeadingDropdown}>
        {dropdownList?.map((item) => (
          <DropdownField.Option key={item.value} value={item.value}>
            {item.label}
          </DropdownField.Option>
        ))}
      </DropdownField>{' '}
    </div>
  );
};

const styles = {
  trayHeading: css`
    display: flex;
    gap: ${theme.spacing(1)};
    flex-wrap: wrap;
  `,
  trayHeadingDropdown: css`
    min-width: ${theme.spacing(20)};
  `,
  trayBody: css`
    overflow-y: auto;
    border-top: 1px solid ${theme.colors.neutral20};
    margin: ${theme.spacing(0, -4)};
    padding: ${theme.spacing(2, 5)};
  `,
};
